import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { DEACTIVATION_REASONS } from "../../constants/cocmDeactivate";

const DeactivatePatientModal = ({
  patient,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const [inactiveReason, setInactiveReason] = useState("");
  const [inactiveReasonOther, setInactiveReasonOther] = useState("");
  const [keepAppAccess, setKeepAppAccess] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (patient) {
      setInactiveReason("");
      setInactiveReasonOther("");
      setKeepAppAccess(true);
      setError("");
    }
  }, [patient]);

  if (!patient) return null;

  const handleConfirm = () => {
    if (!inactiveReason) {
      setError("Please select a deactivation reason");
      return;
    }
    if (inactiveReason === "Other" && !inactiveReasonOther.trim()) {
      setError("Please provide details for Other");
      return;
    }

    onConfirm({
      inactiveReason,
      inactiveReasonOther: inactiveReasonOther.trim(),
      keepAppAccess,
    });
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 1070,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          width: "520px",
          maxWidth: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <div
          style={{
            backgroundColor: "#0A3652",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 700,
              color: "#FFFFFF",
            }}
          >
            Deactivate Patient
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: "none",
              border: "none",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              color: "#FFFFFF",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "14px",
              alignItems: "flex-start",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#FEF2F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FaExclamationTriangle size={18} color="#EF4444" />
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#374151",
                  lineHeight: 1.6,
                }}
              >
                This will move{" "}
                <span style={{ fontWeight: 600, color: "#064260" }}>
                  {patient.name}
                </span>{" "}
                to Inactive. Please provide a reason before continuing.
              </p>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "12px",
                  color: "#6B7280",
                  lineHeight: 1.5,
                }}
              >
                Historical activity, notes, assessments, sessions, and billing
                records will remain available.
              </p>
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="deactivation-reason"
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Deactivation Reason *
            </label>
            <select
              id="deactivation-reason"
              value={inactiveReason}
              onChange={(e) => {
                setInactiveReason(e.target.value);
                if (e.target.value !== "Other") setInactiveReasonOther("");
                if (error) setError("");
              }}
              disabled={isSubmitting}
              style={{
                width: "100%",
                height: "42px",
                borderRadius: "8px",
                border: error ? "1px solid #EF4444" : "1px solid #E5E7EB",
                padding: "0 12px",
                fontSize: "14px",
                color: "#1F2937",
                backgroundColor: "#FFFFFF",
              }}
            >
              <option value="">Select a reason</option>
              {DEACTIVATION_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
            {inactiveReason === "Other" && (
              <textarea
                value={inactiveReasonOther}
                onChange={(e) => {
                  setInactiveReasonOther(e.target.value);
                  if (error) setError("");
                }}
                disabled={isSubmitting}
                placeholder="Please specify..."
                rows={3}
                style={{
                  width: "100%",
                  marginTop: "10px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  padding: "10px 12px",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            )}
            {error && (
              <div
                style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  color: "#EF4444",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            style={{
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "14px 16px",
              marginBottom: "24px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                fontSize: "13px",
                color: "#1F2937",
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                marginBottom: "8px",
              }}
            >
              <input
                type="checkbox"
                checked={keepAppAccess}
                onChange={(e) => setKeepAppAccess(e.target.checked)}
                disabled={isSubmitting}
                style={{ marginTop: "2px" }}
              />
              <span>Keep app access active</span>
            </label>
            <p
              style={{
                margin: 0,
                fontSize: "12px",
                color: "#6B7280",
                lineHeight: 1.5,
                paddingLeft: "26px",
              }}
            >
              The patient may continue using the Numente app even if they are no
              longer active in CoCM. Turn this off only if app access should be
              revoked.
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "12px 16px",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                color: "#374151",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "12px 16px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: isSubmitting ? "#FCA5A5" : "#EF4444",
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              {isSubmitting ? "Deactivating..." : "Confirm Deactivation"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeactivatePatientModal;
